import { supabase } from '@/integrations/supabase/client';

/**
 * Checks if a job is a diagnostic or inspection job based on multiple criteria.
 * Matches case-insensitively against:
 * - category
 * - submenu
 * - proficiency_category
 * - name/label (for "DVI", "Inspection", "Diagnostic")
 */
export const isDiagnosticOrInspectionJob = (job: {
  category?: string;
  submenu?: string;
  proficiency_category?: string;
  name?: string;
  label?: string;
}): boolean => {
  const category = (job.category || '').toLowerCase();
  const submenu = (job.submenu || '').toLowerCase();
  const profCategory = (job.proficiency_category || '').toLowerCase();
  const jobName = (job.name || job.label || '').toLowerCase();
  
  return (
    // Check category
    category === 'diagnostics' || 
    category === 'inspections' ||
    category.includes('diagnostic') ||
    category.includes('inspection') ||
    // Check submenu
    submenu === 'diagnostics' ||
    submenu === 'inspections' ||
    submenu.includes('diagnostic') ||
    submenu.includes('inspection') ||
    // Check proficiency_category
    profCategory === 'diagnostics' ||
    profCategory === 'inspections' ||
    profCategory.includes('diagnostic') ||
    profCategory.includes('inspection') ||
    // Check job name for DVI, diagnostic, inspection
    jobName.includes('dvi') ||
    jobName.includes('diagnostic') ||
    jobName.includes('inspection')
  );
};

interface TicketJobWithInfo {
  job_id: string;
  diagnosis_outcome: string | null;
  separate_time?: boolean;  // Added for pause-lock system
  jobs: {
    id: string;
    name: string;
    category: string;
    submenu: string;
    proficiency_category?: string;
  } | null;
}

/**
 * Fetches ticket jobs with job info for diagnostic detection
 */
export const fetchTicketJobsWithInfo = async (ticketId: string): Promise<TicketJobWithInfo[]> => {
  // First get ticket_jobs
  const { data: ticketJobsData, error: tjError } = await supabase
    .from('ticket_jobs')
    .select('job_id, diagnosis_outcome, separate_time')
    .eq('ticket_id', ticketId);

  if (tjError) {
    console.error('Error fetching ticket jobs:', tjError);
    return [];
  }

  if (!ticketJobsData || ticketJobsData.length === 0) {
    return [];
  }

  // Get job details from the jobs table (no FK exists, so we query separately)
  const jobIds = ticketJobsData.map(tj => tj.job_id);
  const { data: jobsData, error: jobsError } = await supabase
    .from('jobs')
    .select('id, name, category, submenu, proficiency_category')
    .in('id', jobIds);

  if (jobsError) {
    console.error('Error fetching jobs:', jobsError);
  }

  // Build a map for quick lookup
  const jobsMap = new Map((jobsData || []).map(j => [j.id, j]));

  // Combine the data
  return ticketJobsData.map(tj => ({
    job_id: tj.job_id,
    diagnosis_outcome: tj.diagnosis_outcome,
    separate_time: tj.separate_time,
    jobs: jobsMap.get(tj.job_id) || null,
  })) as TicketJobWithInfo[];
};
/**
 * Filters ticket jobs to only those that are diagnostic/inspection and NOT yet time-locked.
 * Used for PAUSE-TIME capture (by technician).
 * Uses separate_time flag to determine if time has already been locked.
 */
export const getUncapturedDiagnosticJobsForPause = (ticketJobs: TicketJobWithInfo[]): TicketJobWithInfo[] => {
  return ticketJobs.filter(job => {
    const jobInfo = job.jobs;
    if (!jobInfo) return false;
    
    const isDiagnostic = isDiagnosticOrInspectionJob({
      category: jobInfo.category,
      submenu: jobInfo.submenu,
      proficiency_category: jobInfo.proficiency_category,
      name: jobInfo.name,
    });
    
    // For pause-time capture: only return jobs that haven't had their time locked yet
    return isDiagnostic && !job.separate_time;
  });
};

/**
 * Filters ticket jobs to only those that are diagnostic/inspection and need outcome recording.
 * Used for COMPLETION-TIME outcome capture (by writer/manager).
 * Checks diagnosis_outcome regardless of whether time was already locked during pause.
 */
export const getJobsNeedingDiagnosticOutcome = (ticketJobs: TicketJobWithInfo[]): TicketJobWithInfo[] => {
  return ticketJobs.filter(job => {
    const jobInfo = job.jobs;
    if (!jobInfo) return false;
    
    const isDiagnostic = isDiagnosticOrInspectionJob({
      category: jobInfo.category,
      submenu: jobInfo.submenu,
      proficiency_category: jobInfo.proficiency_category,
      name: jobInfo.name,
    });
    
    // For completion-time outcome: return jobs missing diagnosis_outcome
    // Regardless of whether technician already captured time during pause
    return isDiagnostic && !job.diagnosis_outcome;
  });
};

/**
 * @deprecated Use getUncapturedDiagnosticJobsForPause or getJobsNeedingDiagnosticOutcome instead
 */
export const getUnresolvedDiagnosticJobs = getJobsNeedingDiagnosticOutcome;

/**
 * Checks if a ticket has any unresolved diagnostic/inspection jobs
 */
export const ticketNeedsDiagnosticOutcome = async (ticketId: string): Promise<boolean> => {
  const jobs = await fetchTicketJobsWithInfo(ticketId);
  const unresolvedJobs = getUnresolvedDiagnosticJobs(jobs);
  return unresolvedJobs.length > 0;
};

/**
 * Helper to format diagnostic jobs for modal display
 */
const formatJobsForModal = (ticketJobs: TicketJobWithInfo[]): Array<{ 
  jobId: string; 
  jobName: string;
  lockType: 'diagnostic' | 'inspection';
}> => {
  return ticketJobs.map(job => {
    const jobInfo = job.jobs;
    const jobName = jobInfo?.name || 'Unknown Job';
    const category = (jobInfo?.category || '').toLowerCase();
    const submenu = (jobInfo?.submenu || '').toLowerCase();
    const name = jobName.toLowerCase();
    
    // Determine if this is an inspection vs diagnostic
    const isInspection = 
      category.includes('inspection') ||
      submenu.includes('inspection') ||
      name.includes('inspection') ||
      name.includes('dvi');
    
    return {
      jobId: job.job_id,
      jobName,
      lockType: isInspection ? 'inspection' : 'diagnostic' as const,
    };
  });
};

/**
 * Prepares diagnostic jobs for PAUSE-TIME modal display (technician capture)
 * Returns jobs that haven't had their time locked yet
 */
export const getUncapturedDiagnosticJobsForPauseModal = async (ticketId: string): Promise<Array<{ 
  jobId: string; 
  jobName: string;
  lockType: 'diagnostic' | 'inspection';
}>> => {
  const jobs = await fetchTicketJobsWithInfo(ticketId);
  const uncapturedJobs = getUncapturedDiagnosticJobsForPause(jobs);
  return formatJobsForModal(uncapturedJobs);
};

/**
 * Prepares diagnostic jobs for COMPLETION-TIME modal display (writer/manager outcome)
 * Returns jobs that haven't had diagnosis_outcome recorded yet
 */
export const getJobsNeedingOutcomeForModal = async (ticketId: string): Promise<Array<{ 
  jobId: string; 
  jobName: string;
  lockType: 'diagnostic' | 'inspection';
}>> => {
  const jobs = await fetchTicketJobsWithInfo(ticketId);
  const jobsNeedingOutcome = getJobsNeedingDiagnosticOutcome(jobs);
  return formatJobsForModal(jobsNeedingOutcome);
};

/**
 * @deprecated Use getUncapturedDiagnosticJobsForPauseModal or getJobsNeedingOutcomeForModal instead
 */
export const getUnresolvedDiagnosticJobsForModal = getJobsNeedingOutcomeForModal;

/**
 * Client-side sync function that checks if all diagnostic/inspection jobs have outcomes
 * and updates the needs_diagnostic_outcome flag accordingly.
 * 
 * Called after the technician records the diagnostic outcome on the
 * authenticated capture path. No completion surface writes it.
 */
export const syncDiagnosticOutcomeFlag = async (ticketId: string): Promise<void> => {
  try {
    const needsOutcome = await ticketNeedsDiagnosticOutcome(ticketId);
    
    await supabase
      .from('tickets')
      .update({ needs_diagnostic_outcome: needsOutcome })
      .eq('id', ticketId);
      
    console.log(`[DiagnosticSync] Ticket ${ticketId} needs_diagnostic_outcome set to ${needsOutcome}`);
  } catch (error) {
    console.error('[DiagnosticSync] Error syncing diagnostic outcome flag:', error);
  }
};

/**
 * Sets the needs_diagnostic_outcome flag to true when a ticket is completed
 * and contains unresolved diagnostic jobs.
 * 
 * Called during ticket completion transition.
 */
export const setDiagnosticOutcomeFlagOnCompletion = async (ticketId: string): Promise<boolean> => {
  const needsOutcome = await ticketNeedsDiagnosticOutcome(ticketId);
  
  if (needsOutcome) {
    await supabase
      .from('tickets')
      .update({ needs_diagnostic_outcome: true })
      .eq('id', ticketId);
      
    console.log(`[DiagnosticSync] Ticket ${ticketId} marked needs_diagnostic_outcome=true on completion`);
  }
  
  return needsOutcome;
};
